import React from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  FormControl,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import { useState } from "react";

const SafeDocumentsForm = ({ onClose }) => {
  const [documents, setDocuments] = useState([
    { docNo: 56, category: "", document: "", condition: "Good" },
  ]);

  // Mock options for dropdowns
  const clientOptions = [{ label: "Choose a client...", value: "" }];
  const fileOptions = [{ label: "Other", value: "Other" }];
  const safeOptions = [{ label: "Choose a Safe...", value: "" }];
  const receivedFromOptions = [{ label: "", value: "" }];
  const approverOptions = [{ label: "Choose an Approver...", value: "" }];
  const categoryOptions = [{ label: "Select Category", value: "" }];
  // const conditionOptions = [
  //   { label: "Good", value: "Good" },
  //   { label: "Fair", value: "Fair" },
  //   { label: "Poor", value: "Poor" },
  // ];

  const validationSchema = yup.object({
    date: yup.string().required("Date is required"),
    client: yup.string().required("Client is required"),
    file: yup.string().required("File is required"),
    safeName: yup.string().required("Safe Name is required"),
    refNoInSafe: yup.number().required("Ref No. in Safe is required"),
    receivedFrom: yup.string().required("Received From is required"),
    reason: yup.string().required("Reason is required"),
    approver: yup.string().required("Approver is required"),
  });

  const formik = useFormik({
    initialValues: {
      date: "",
      client: "",
      file: "Other",
      safeName: "",
      refNoInSafe: 0,
      receivedFrom: "",
      reason: "",
      approver: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Mock submission
      Swal.fire({
        icon: "success",
        title: "Documents Registered",
        text: "Your document(s) have been added successfully.",
      });
      onClose();
    },
  });

  const addDocument = () => {
    const newDocNo = Math.max(...documents.map((d) => d.docNo || 0), 0) + 1;
    setDocuments([...documents, { docNo: newDocNo, category: "", document: "", condition: "Good" }]);
  };

  const removeDocument = (index) => {
    if (documents.length > 1) {
      setDocuments(documents.filter((_, i) => i !== index));
    } else {
      Swal.fire("Warning", "At least one document is required.", "warning");
    }
  };

  const updateDocumentField = (index, field, value) => {
    const newDocs = [...documents];
    newDocs[index][field] = value;
    setDocuments(newDocs);
  };

  const handleAutocompleteChange = (name, value) => {
    formik.setFieldValue(name, value ? value.value : "");
  };

  return (
    <Paper
      sx={{
        p: 3,
        backgroundColor: "#1a1a1a",
        border: "1px solid #333",
        borderRadius: 2,
        boxShadow: 3,
        minWidth: 600,
        color: "#fff",
      }}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{
          backgroundColor: "#333",
          color: "#fff",
          p: 1.5,
          borderRadius: 1,
          mb: 3,
          fontWeight: "bold",
        }}
      >
        Register Document(s) in Safe
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Box display="grid" gridTemplateColumns="200px 1fr" gap={1} alignItems="start">
          {/* Date */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Date *</Typography>
          <TextField
            fullWidth
            type="date"
            name="date"
            value={formik.values.date}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.date && Boolean(formik.errors.date)}
            helperText={formik.touched.date && formik.errors.date}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              "& .MuiOutlinedInput-root": { 
                height: 40,
                backgroundColor: "#333",
                color: "#fff",
                "& fieldset": { borderColor: "#555" },
                "&:hover fieldset": { borderColor: "#777" },
                "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
              },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiInputBase-input": { color: "#fff" },
            }}
          />

          {/* Client */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Client *</Typography>
          <FormControl fullWidth error={formik.touched.client && Boolean(formik.errors.client)}>
            <Autocomplete
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              value={clientOptions.find((opt) => opt.value === formik.values.client) || null}
              onChange={(e, value) => handleAutocompleteChange("client", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a client..."
                  error={formik.touched.client && Boolean(formik.errors.client)}
                  helperText={formik.touched.client && formik.errors.client}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      height: 40,
                      backgroundColor: "#333",
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#777" },
                      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                    },
                    "& .MuiInputLabel-root": { color: "#ccc" },
                    "& .MuiInputBase-input": { color: "#fff" },
                  }}
                />
              )}
            />
          </FormControl>

          {/* File */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>File *</Typography>
          <FormControl fullWidth error={formik.touched.file && Boolean(formik.errors.file)}>
            <Autocomplete
              options={fileOptions}
              getOptionLabel={(option) => option.label}
              value={fileOptions.find((opt) => opt.value === formik.values.file) || null}
              onChange={(e, value) => handleAutocompleteChange("file", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a file..."
                  error={formik.touched.file && Boolean(formik.errors.file)}
                  helperText={formik.touched.file && formik.errors.file}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      height: 40,
                      backgroundColor: "#333",
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#777" },
                      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                    },
                    "& .MuiInputLabel-root": { color: "#ccc" },
                    "& .MuiInputBase-input": { color: "#fff" },
                  }}
                />
              )}
            />
          </FormControl>

          {/* Safe Name */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Safe Name *</Typography>
          <FormControl fullWidth error={formik.touched.safeName && Boolean(formik.errors.safeName)}>
            <Autocomplete
              options={safeOptions}
              getOptionLabel={(option) => option.label}
              value={safeOptions.find((opt) => opt.value === formik.values.safeName) || null}
              onChange={(e, value) => handleAutocompleteChange("safeName", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose a Safe..."
                  error={formik.touched.safeName && Boolean(formik.errors.safeName)}
                  helperText={formik.touched.safeName && formik.errors.safeName}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      height: 40,
                      backgroundColor: "#333",
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#777" },
                      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                    },
                    "& .MuiInputLabel-root": { color: "#ccc" },
                    "& .MuiInputBase-input": { color: "#fff" },
                  }}
                />
              )}
            />
          </FormControl>

          {/* Ref No. in Safe */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Ref No. in Safe *</Typography>
          <TextField
            fullWidth
            type="number"
            name="refNoInSafe"
            value={formik.values.refNoInSafe}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.refNoInSafe && Boolean(formik.errors.refNoInSafe)}
            helperText={formik.touched.refNoInSafe && formik.errors.refNoInSafe || "Double click the textbox if ONLY you need to edit this Ref No."}
            sx={{ 
              "& .MuiOutlinedInput-root": { 
                height: 40,
                backgroundColor: "#333",
                color: "#fff",
                "& fieldset": { borderColor: "#555" },
                "&:hover fieldset": { borderColor: "#777" },
                "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
              },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiInputBase-input": { color: "#fff" },
            }}
          />

          {/* Received From */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Received From *</Typography>
          <FormControl fullWidth error={formik.touched.receivedFrom && Boolean(formik.errors.receivedFrom)}>
            <Autocomplete
              options={receivedFromOptions}
              getOptionLabel={(option) => option.label}
              value={receivedFromOptions.find((opt) => opt.value === formik.values.receivedFrom) || null}
              onChange={(e, value) => handleAutocompleteChange("receivedFrom", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose received from..."
                  error={formik.touched.receivedFrom && Boolean(formik.errors.receivedFrom)}
                  helperText={formik.touched.receivedFrom && formik.errors.receivedFrom}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      height: 40,
                      backgroundColor: "#333",
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#777" },
                      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                    },
                    "& .MuiInputLabel-root": { color: "#ccc" },
                    "& .MuiInputBase-input": { color: "#fff" },
                  }}
                />
              )}
            />
          </FormControl>

          {/* Reason */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Reason *</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            name="reason"
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
            helperText={formik.touched.reason && formik.errors.reason}
            sx={{ 
              gridColumn: "2",
              "& .MuiOutlinedInput-root": { 
                backgroundColor: "#333",
                color: "#fff",
                "& fieldset": { borderColor: "#555" },
                "&:hover fieldset": { borderColor: "#777" },
                "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
              },
              "& .MuiInputLabel-root": { color: "#ccc" },
              "& .MuiInputBase-input": { color: "#fff" },
            }}
          />

          {/* Approver */}
          <Typography sx={{ color: "#fff", fontWeight: "bold" }}>Approver *</Typography>
          <FormControl fullWidth error={formik.touched.approver && Boolean(formik.errors.approver)}>
            <Autocomplete
              options={approverOptions}
              getOptionLabel={(option) => option.label}
              value={approverOptions.find((opt) => opt.value === formik.values.approver) || null}
              onChange={(e, value) => handleAutocompleteChange("approver", value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose an Approver..."
                  error={formik.touched.approver && Boolean(formik.errors.approver)}
                  helperText={formik.touched.approver && formik.errors.approver}
                  sx={{ 
                    "& .MuiOutlinedInput-root": { 
                      height: 40,
                      backgroundColor: "#333",
                      color: "#fff",
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#777" },
                      "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                    },
                    "& .MuiInputLabel-root": { color: "#ccc" },
                    "& .MuiInputBase-input": { color: "#fff" },
                  }}
                />
              )}
            />
          </FormControl>
        </Box>

        {/* <Box mt={3}>
          <Typography variant="h4" sx={{ color: "#fff", mb: 2, fontWeight: "bold", textAlign: "center" }}>
            Documents
          </Typography>
          <TableContainer
            sx={{
              maxHeight: 200,
              border: "1px solid #555",
              borderRadius: 1,
              backgroundColor: "#2a2a2a",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#333" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Doc No</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Category</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Document</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Condition</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc, i) => (
                  <TableRow key={i} sx={{ "&:hover": { backgroundColor: "#404040" } }}>
                    <TableCell sx={{ color: "#fff" }}>{doc.docNo}</TableCell>
                    <TableCell>
                      <Autocomplete
                        options={categoryOptions}
                        getOptionLabel={(option) => option.label}
                        value={categoryOptions.find((opt) => opt.value === doc.category) || null}
                        onChange={(_, value) => updateDocumentField(i, "category", value ? value.value : "")}
                        size="small"
                        sx={{ 
                          "& .MuiOutlinedInput-root": { 
                            backgroundColor: "#333",
                            color: "#fff",
                            "& fieldset": { borderColor: "#555" },
                            "&:hover fieldset": { borderColor: "#777" },
                            "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                          },
                          "& .MuiInputBase-input": { color: "#fff" },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#333", color: "#fff" } }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={doc.document}
                        onChange={(e) => updateDocumentField(i, "document", e.target.value)}
                        sx={{ 
                          "& .MuiOutlinedInput-root": { 
                            backgroundColor: "#333",
                            color: "#fff",
                            "& fieldset": { borderColor: "#555" },
                            "&:hover fieldset": { borderColor: "#777" },
                            "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                          },
                          "& .MuiInputBase-input": { color: "#fff" },
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        options={conditionOptions}
                        getOptionLabel={(option) => option.label}
                        value={conditionOptions.find((opt) => opt.value === doc.condition) || null}
                        onChange={(_, value) => updateDocumentField(i, "condition", value ? value.value : "")}
                        size="small"
                        sx={{ 
                          "& .MuiOutlinedInput-root": { 
                            backgroundColor: "#333",
                            color: "#fff",
                            "& fieldset": { borderColor: "#555" },
                            "&:hover fieldset": { borderColor: "#777" },
                            "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                          },
                          "& .MuiInputBase-input": { color: "#fff" },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#333", color: "#fff" } }}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton color="error" size="small" onClick={() => removeDocument(i)}>
                        <DeleteIcon fontSize="small" sx={{ color: "#ff4444" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addDocument}
            sx={{ 
              mt: 1, 
              borderColor: "#0d47a1", 
              color: "#0d47a1",
              "&:hover": { borderColor: "#0a8d15", color: "#0a8d15" },
            }}
          >
            New Document
          </Button>
        </Box> */}

        <Box display="flex" justifyContent="center" gap={2} mt={3}>
          <Button
            type="submit"
            variant="contained"
            sx={{ 
              backgroundColor: "#4caf50", 
              color: "#fff", 
              px: 4,
              "&:hover": { backgroundColor: "#45a049" },
            }}
          >
            Add New Document(s)
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ 
              borderColor: "#fff", 
              color: "#fff", 
              px: 4,
              "&:hover": { borderColor: "#ccc", color: "#ccc" },
            }}
          >
            Close
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default SafeDocumentsForm;